import React, { useState, useEffect } from 'react';
import { driverApi } from '../../config/driverApi';
import { 
  Clock, 
  DollarSign, 
  Star,
  AlertTriangle,
  Calendar
} from 'lucide-react';

const DriverHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    fetchHistory();
  }, [period]);

  const fetchHistory = async () => {
    try {
      const res = await driverApi.get(`/history.php?action=history&period=${period}`);
      if (res.data.success) {
        setHistory(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      delivered: '#4CAF50',
      cancelled: '#F44336',
      rejected: '#F44336',
      assigned: '#FFC107',
      accepted: '#2196F3',
      picked_up: '#FF9800',
    };
    return colors[status] || '#999';
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid #f0f0f0',
          borderTopColor: '#D4AF37',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <p>Loading history...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '1.5rem',
          fontWeight: 700,
          color: '#1A1A1A',
          marginBottom: '12px',
        }}>
          Delivery History
        </h2>

        {/* Period Filter */}
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          paddingBottom: '4px',
        }}>
          {[
            { value: 'all', label: 'All Time' },
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
          ].map((p) => (
            <button
              key={p.value}
              onClick={() => { setPeriod(p.value); setLoading(true); }}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid #ddd',
                backgroundColor: period === p.value ? '#D4AF37' : '#fff',
                color: period === p.value ? '#fff' : '#666',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {history.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#999',
        }}>
          <Clock size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p style={{ fontSize: '1rem', marginBottom: '8px' }}>No deliveries yet</p>
          <p style={{ fontSize: '0.85rem' }}>Your delivery history will appear here</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {history.map((item) => (
            <div
              key={item.history_id}
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #EAEDF1',
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px',
              }}>
                <div>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#1A1A1A',
                    margin: '0 0 4px',
                  }}>
                    Order #{item.order_id}
                  </h3>
                  <p style={{
                    fontSize: '0.8rem',
                    color: '#666',
                    margin: 0,
                  }}>
                    {new Date(item.assigned_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor: getStatusColor(item.status) + '20',
                  color: getStatusColor(item.status),
                  textTransform: 'capitalize',
                }}>
                  {item.status}
                </span>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                marginBottom: '12px',
              }}>
                <p style={{ fontSize: '0.85rem', color: '#333', margin: 0 }}>
                  {item.customer_name} - {item.customer_phone}
                </p>
                <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>
                  {item.delivery_address?.substring(0, 60)}{item.delivery_address?.length > 60 ? '...' : ''}
                </p>
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}>
                  <span style={{ fontSize: '0.85rem', color: '#666' }}>
                    Order: UGX {Number(item.order_total).toLocaleString()}
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#4CAF50', fontWeight: 600 }}>
                    Fee: UGX {Number(item.delivery_fee).toLocaleString()}
                  </span>
                  {item.total_duration_minutes && (
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>
                      Duration: {item.total_duration_minutes} min
                    </span>
                  )}
                </div>
              </div>

              {item.customer_rating && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 0',
                  borderTop: '1px solid #f0f0f0',
                }}>
                  <Star size={16} color="#FFC107" fill="#FFC107" />
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                    {item.customer_rating}/5
                  </span>
                </div>
              )}

              {item.issue_reported && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 0',
                  borderTop: '1px solid #f0f0f0',
                  color: '#F44336',
                }}>
                  <AlertTriangle size={16} />
                  <span style={{ fontSize: '0.85rem' }}>
                    {item.issue_type}{item.issue_notes ? `: ${item.issue_notes}` : ''}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DriverHistory;
